import { CommonModule } from '@angular/common';
import { ModuleWithProviders, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AumicoComponentsModule } from '@modeso/aumico-components';
import { AumicoCoreModule } from '@modeso/aumico-lib-core-fe';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { I18NextModule } from 'angular-i18next';
import { LucideAngularModule } from 'lucide-angular';
import {
    AumicoIntegrationsConfig,
    AumicoIntegrationsConfigService,
} from './aumico-integrations.config';
import { AbacusConnectModalComponent } from './components/abacus-connect-modal/abacus-connect-modal.component';
import { AbacusManageModalComponent } from './components/abacus-manage-modal/abacus-manage-modal.component';
import { AbacusMappingModalComponent } from './components/abacus-mapping-modal/abacus-mapping-modal.component';
import { CompanyIntegrationsOverviewComponent } from './components/company-integrations-overview/company-integrations-overview.component';
import { CompanyMappingModalComponent } from './components/company-mapping-modal/company-mapping-modal.component';
import { D365BcConnectModalComponent } from './components/d365bc-connect-modal/d365bc-connect-modal.component';
import { D365BcManageModalComponent } from './components/d365bc-manage-modal/d365bc-manage-modal.component';
import { IntegrationGoToAdminModalComponent } from './components/integration-go-to-admin-modal/integration-go-to-admin-modal.component';
import { IntegrationCardComponent } from './components/integration-card/integration-card.component';
import { IntegrationSyncCardComponent } from './components/integration-sync-card/integration-sync-card.component';
import { IntegrationsSearchCompanyInputFieldComponent } from './components/integrations-search-company-input-field/integrations-search-company-input-field.component';
import { IntegrationsOverviewComponent } from './components/integrations-overview/integrations-overview.component';
import { OutboundApiSectionComponent } from './components/outbound-api-section/outbound-api-section.component';
import { PartnerClientCreateModalComponent } from './components/partner-client-create-modal/partner-client-create-modal.component';
import { PartnerConnectModalComponent } from './components/partner-connect-modal/partner-connect-modal.component';
import { PartnerConnectSectionComponent } from './components/partner-connect-section/partner-connect-section.component';
import { PartnerClientInfoModalComponent } from './components/partner-client-info-modal/partner-client-info-modal.component';
import { SharepointConnectModalComponent } from './components/sharepoint-connect-modal/sharepoint-connect-modal.component';
import { SharepointManageModalComponent } from './components/sharepoint-manage-modal/sharepoint-manage-modal.component';
import { SharepointFoldersComponent } from './components/sharepoint-folders/sharepoint-folders.component';
import { SharepointFoldersContainerComponent } from './components/sharepoint-folders-container/sharepoint-folders-container.component';
import { TopalConnectModalComponent } from './components/topal-connect-modal/topal-connect-modal.component';
import { TopalManageModalComponent } from './components/topal-manage-modal/topal-manage-modal.component';
import { AccountoConnectModalComponent } from './components/accounto-connect-modal/accounto-connect-modal.component';
import { AccountoManageModalComponent } from './components/accounto-manage-modal/accounto-manage-modal.component';
import { ProffixConnectModalComponent } from './components/proffix-connect-modal/proffix-connect-modal.component';
import { ProffixManageModalComponent } from './components/proffix-manage-modal/proffix-manage-modal.component';
import { VendorOrgRedirectModalComponent } from './components/vendor-org-redirect-modal/vendor-org-redirect-modal.component';
import { IntegrationEffects } from './effects/integration.effects';
import { PartnerClientEffects } from './effects/partner-client.effects';
import { INTEGRATION_FEATURE_KEY } from './reducer/integration.feature-key';
import { integrationReducer } from './reducer/integration.reducer';
import { PARTNER_CLIENT_FEATURE_KEY } from './reducer/partner-client.feature-key';
import { partnerClientReducer } from './reducer/partner-client.reducer';
import { IntegrationService } from './services/integration.service';
import { PartnerClientService } from './services/partner-client.service';
import { PartnerConnectionService } from './services/partner-connection.service';
import {
    DefaultSharePointFilePickerService,
    SharePointFilePickerService,
} from './services/sharepoint-file-picker.service';

const EXPORTED_DECLARATIONS = [
    IntegrationCardComponent,
    IntegrationSyncCardComponent,
    IntegrationsSearchCompanyInputFieldComponent,
    IntegrationsOverviewComponent,
    OutboundApiSectionComponent,
    PartnerConnectSectionComponent,
    PartnerConnectModalComponent,
    PartnerClientCreateModalComponent,
    PartnerClientInfoModalComponent,
    CompanyIntegrationsOverviewComponent,
    VendorOrgRedirectModalComponent,
    D365BcConnectModalComponent,
    D365BcManageModalComponent,
    AbacusConnectModalComponent,
    AbacusManageModalComponent,
    AbacusMappingModalComponent,
    TopalConnectModalComponent,
    TopalManageModalComponent,
    AccountoConnectModalComponent,
    AccountoManageModalComponent,
    ProffixConnectModalComponent,
    ProffixManageModalComponent,
    CompanyMappingModalComponent,
    IntegrationGoToAdminModalComponent,
    SharepointConnectModalComponent,
    SharepointManageModalComponent,
    SharepointFoldersComponent,
    SharepointFoldersContainerComponent,
];

@NgModule({
    declarations: EXPORTED_DECLARATIONS,
    imports: [
        AumicoCoreModule,
        AumicoComponentsModule,
        CommonModule,
        I18NextModule,
        FormsModule,
        ReactiveFormsModule,
        LucideAngularModule,
        StoreModule.forFeature(INTEGRATION_FEATURE_KEY, integrationReducer),
        StoreModule.forFeature(PARTNER_CLIENT_FEATURE_KEY, partnerClientReducer),
        EffectsModule.forFeature([IntegrationEffects, PartnerClientEffects]),
    ],
    exports: EXPORTED_DECLARATIONS,
    providers: [],
})
export class AumicoLibIntegrationsFeModule {
    public static forRoot(config: AumicoIntegrationsConfig): ModuleWithProviders<AumicoLibIntegrationsFeModule> {
        return {
            ngModule: AumicoLibIntegrationsFeModule,
            providers: [
                IntegrationService,
                PartnerClientService,
                PartnerConnectionService,
                // Real Microsoft File Picker v8 impl behind the injectable seam
                // (LIVE-VERIFY). Tests override with MockSharePointFilePickerService.
                {
                    provide: SharePointFilePickerService,
                    useClass: DefaultSharePointFilePickerService,
                },
                {
                    provide: AumicoIntegrationsConfigService,
                    useValue: config,
                },
            ],
        };
    }
}
